import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Autocomplete,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { CalendarToday, CheckCircle, Error } from '@mui/icons-material';
import { format as formatRut } from 'rut.js';
import useStyles from '../styles';
import { EnrollmentFormData } from '../types';
import { StepConfig, MAX_FIELD_LENGTH } from '../formConfig';
import { ValidationError, validateEmailWithBackend } from '../validations';

interface DynamicFormProps {
  stepConfig: StepConfig;
  formData: EnrollmentFormData;
  onFormChange: (data: Partial<EnrollmentFormData>) => void;
  validationErrors: ValidationError[];
}

interface EmailValidationState {
  isValidating: boolean;
  message: string;
  isValid: boolean;
  canContinue: boolean;
}

function DynamicForm({
  stepConfig, formData, onFormChange, validationErrors,
}: DynamicFormProps) {
  const classes = useStyles();

  const [
    emailValidations,
    setEmailValidations,
  ] = useState<Record<string, EmailValidationState>>({});

  const handleChange = (fieldName: string, fieldType: string) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value: inputValue, checked } = event.target;
    let value: string | number | boolean | null = inputValue;

    if (fieldType === 'checkbox') {
      value = checked;
    } else if (fieldType === 'year') {
      value = inputValue === '' ? null : Number(inputValue);
    } else if (['rut', 'representativeRut', 'supporterRut'].includes(fieldName)) {
      const cleaned = inputValue.replace(/[^0-9kK]/g, '');
      value = cleaned ? formatRut(cleaned) : '';
    }

    const capitalizedFields = [
      'name',
      'lastname',
      'motherLastname',
      'representativeName',
      'representativeLastname',
      'representativeMotherLastname',
      'supporterName',
      'supporterLastname',
      'supporterMotherLastname',
      'leadName',
    ];
    if (
      fieldType === 'text'
      && capitalizedFields.includes(fieldName)
      && typeof value === 'string'
    ) {
      const endsWithSpace = value.endsWith(' ');

      const capitalizeWords = (text: string) => text
        .toLowerCase()
        .split(' ')
        .filter((word) => word.length > 0)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      let normalized = capitalizeWords(value.trim());

      if (endsWithSpace) {
        normalized = `${normalized} `;
      }

      value = normalized;
    }

    onFormChange({ [fieldName]: value });
  };

  const handleDateChange = (fieldName: string) => (date: Date | null) => {
    onFormChange({ [fieldName]: date });
  };

  const handleSelectChange = (fieldName: string) => (
    event: SelectChangeEvent<string | string[]>,
  ) => {
    const { value } = event.target as { value: string | string[] };

    if (fieldName === 'preferredLocationIds') {
      const rawValues: string[] = Array.isArray(value) ? (value as string[]) : [String(value)];
      const numericIds = rawValues
        .filter((v) => v !== '')
        .map((v) => Number(v))
        .filter((v) => !Number.isNaN(v));

      const update: Partial<EnrollmentFormData> = {
        preferredLocationIds: numericIds,
      };

      // Si ya tenía Egresados seleccionado y ninguna sede es Escuela Militar, limpiar nivel
      if (formData.desiredClassYear === 'Egresados') {
        const locationField = stepConfig.fields.find(
          (f) => f.name === 'preferredLocationIds',
        );
        const locationOptions = locationField?.options || [];
        const selectedLocations = locationOptions.filter((opt) => numericIds
          .some((id) => String(id) === String(opt.value)));
        const hasEscuelaMilitar = selectedLocations
          .some((opt) => opt.label === 'Escuela Militar');
        if (!hasEscuelaMilitar) {
          update.desiredClassYear = '';
        }
      }

      onFormChange(update);
      return;
    }

    const finalValue = Array.isArray(value) ? String(value[0]) : String(value);

    const update: Partial<EnrollmentFormData> = {
      [fieldName]: finalValue,
    };

    onFormChange(update);
  };

  // Debounce para validación de email
  useEffect(() => {
    const emailFields = ['email', 'representativeEmail', 'supporterEmail'];
    const timeoutIds: NodeJS.Timeout[] = [];

    emailFields.forEach((fieldName) => {
      const emailValue = formData[fieldName as keyof EnrollmentFormData] as string;
      if (emailValue) {
        const timeoutId = setTimeout(() => {
          validateEmailWithBackend(fieldName, emailValue, formData, setEmailValidations);
        }, 1000);

        timeoutIds.push(timeoutId);
      }
    });

    return () => {
      timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, [
    formData.email, formData.rut,
    formData.representativeEmail, formData.representativeRut,
    formData.supporterEmail, formData.supporterRut,
  ]);

  const renderField = (field: StepConfig['fields'][0]) => {
    let fieldValue = formData[field.name as keyof EnrollmentFormData];

    if (field.name.startsWith('subject_')) {
      const subjectId = field.name.replace('subject_', '');
      fieldValue = formData.selectedSubjects.includes(subjectId);
    }

    const fieldError = validationErrors.find((error) => error.field === field.name);
    const hasError = Boolean(fieldError);

    const isSostenedorField = field.name.includes('supporter');
    const isDisabled = isSostenedorField && formData.isSameAsRepresentative && field.name !== 'isSameAsRepresentative';

    // Manejo de showWhenSchoolIsOtro (backward compatibility)
    if (field.showWhenSchoolIsOtro && stepConfig.stepNumber === 1) {
      const schoolField = stepConfig.fields.find((f) => f.name === 'schoolId');
      const selectedSchoolOption = schoolField?.options?.find(
        (o) => String(o.value) === String(formData.schoolId),
      );
      if (selectedSchoolOption?.label !== 'Otro') {
        return null;
      }
    }

    // Manejo genérico de showWhen
    if (field.showWhen) {
      const { field: conditionField, hasLabel } = field.showWhen;
      const targetField = stepConfig.fields.find((f) => f.name === conditionField);
      const targetFieldValue = formData[conditionField as keyof EnrollmentFormData];
      const selectedOption = targetField?.options?.find(
        (o) => String(o.value) === String(targetFieldValue),
      );
      if (selectedOption?.label !== hasLabel) {
        return null;
      }
    }

    if (stepConfig.stepNumber === 6) {
      // Mostrar campo de código solo para Embajador(a) y Centro de alumnos
      if (field.name === 'ambassadorCode') {
        if (formData.leadSource !== 'embajador' && formData.leadSource !== 'centro_alumnos') {
          return null;
        }

        let dynamicLabel = 'Código';
        let dynamicPlaceholder = 'Ingresa el código';

        if (formData.leadSource === 'embajador') {
          dynamicLabel = 'Código del embajador/a';
          dynamicPlaceholder = 'Ingresa el código del embajador/a';
        } else if (formData.leadSource === 'centro_alumnos') {
          dynamicLabel = 'Código del centro de alumnos';
          dynamicPlaceholder = 'Ingresa el código del centro de alumnos';
        }

        return (
          <TextField
            key={field.name}
            fullWidth
            label={dynamicLabel}
            type={field.type}
            variant="outlined"
            value={String(fieldValue || '')}
            onChange={handleChange(field.name, field.type)}
            placeholder={dynamicPlaceholder}
            required={field.required}
            className={classes.textField}
            error={hasError}
            helperText={fieldError?.message}
            disabled={isDisabled}
            inputProps={{ maxLength: field.maxLength ?? MAX_FIELD_LENGTH }}
          />
        );
      }

      // Mostrar campo de nombre solo para Hermano(a) y Profesor(a)
      if (field.name === 'leadName') {
        if (formData.leadSource !== 'hermano' && formData.leadSource !== 'profesor') {
          return null;
        }

        let dynamicLabel = 'Nombre';
        let dynamicPlaceholder = 'Ingresa el nombre';

        if (formData.leadSource === 'hermano') {
          dynamicLabel = 'Nombre del hermano/a';
          dynamicPlaceholder = 'Ingresa el nombre de tu hermano/a';
        } else if (formData.leadSource === 'profesor') {
          dynamicLabel = 'Nombre del profesor/a';
          dynamicPlaceholder = 'Ingresa el nombre del profesor/a';
        }

        return (
          <TextField
            key={field.name}
            fullWidth
            label={dynamicLabel}
            type={field.type}
            variant="outlined"
            value={String(fieldValue || '')}
            onChange={handleChange(field.name, field.type)}
            placeholder={dynamicPlaceholder}
            required={field.required}
            className={classes.textField}
            error={hasError}
            helperText={fieldError?.message}
            disabled={isDisabled}
            inputProps={{ maxLength: field.maxLength ?? MAX_FIELD_LENGTH }}
          />
        );
      }
    }
    if (field.type === 'checkbox') {
      return (
        <FormControlLabel
          key={field.name}
          control={
            <Checkbox
              checked={Boolean(fieldValue)}
              onChange={handleChange(field.name, field.type)}
              color="primary"
            />
          }
          label={field.label}
          className={classes.checkboxLabel}
        />
      );
    }

    if (field.type === 'textarea') {
      return (
        <Box key={field.name}>
          {field.label && (
            <Typography gutterBottom className={classes.textareaLabel}>
              {field.label}
            </Typography>
          )}
          <TextField
            fullWidth
            multiline
            rows={field.rows || 4}
            variant="outlined"
            value={String(fieldValue || '')}
            onChange={handleChange(field.name, field.type)}
            placeholder={field.placeholder}
            className={classes.textareaField}
            error={hasError}
            helperText={fieldError?.message}
            disabled={isDisabled}
            inputProps={{ maxLength: field.maxLength ?? MAX_FIELD_LENGTH }}
          />
        </Box>
      );
    }

    if (field.type === 'date') {
      return (
        <DatePicker
          key={field.name}
          label={field.label}
          value={fieldValue ? new Date(fieldValue as string) : null}
          onChange={handleDateChange(field.name)}
          slots={{
            openPickerIcon: CalendarToday,
          }}
          className={classes.datePicker}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: 'outlined',
              required: field.required,
              className: classes.textField,
              error: hasError,
              helperText: fieldError?.message,
              disabled: isDisabled,
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarToday
                      sx={{
                        color: 'grey.500',
                        cursor: 'pointer',
                      }}
                    />
                  </InputAdornment>
                ),
              },
            },
            openPickerButton: {
              sx: { display: 'none' },
            },
          }}
        />
      );
    }

    if (field.type === 'select') {
      let options = field.options || [];

      if (field.name === 'desiredClassYear') {
        const locationField = stepConfig.fields.find(
          (f) => f.name === 'preferredLocationIds',
        );

        const locationOptions = locationField?.options || [];
        const selectedLocations = locationOptions.filter(
          (opt) => (formData.preferredLocationIds || [])
            .some((id) => String(id) === String(opt.value)),
        );

        const hasEscuelaMilitar = selectedLocations
          .some((opt) => opt.label === 'Escuela Militar');

        if (!hasEscuelaMilitar) {
          options = options.filter((option) => option.value !== 'Egresados');
        }
      }

      const isMultiple = Boolean(field.multiple);

      return (
        <FormControl
          key={field.name}
          fullWidth
          variant="outlined"
          required={field.required}
          className={classes.textField}
          error={hasError}
          disabled={isDisabled}
        >
          <InputLabel>{field.label}</InputLabel>
          <Select<string | string[]>
            multiple={isMultiple}
            value={
              isMultiple
                ? (formData.preferredLocationIds || []).map((id) => String(id))
                : String(fieldValue || '')
            }
            onChange={handleSelectChange(field.name)}
            label={field.label}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {hasError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {fieldError?.message}
            </Typography>
          )}
          {!hasError && field.name === 'preferredLocationIds' && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, ml: 1.75 }}>
              Puedes seleccionar una o más sedes.
            </Typography>
          )}
        </FormControl>
      );
    }

    if (field.type === 'autocomplete') {
      const currentOption = field.options?.find((opt) => opt.value === String(fieldValue || '')) || null;

      const filterOptions = (
        options: { label: string; value: string }[],
        { inputValue }: { inputValue: string },
      ) => {
        const normalizedInput = inputValue.trim().toLowerCase();
        let filtered = options.filter((opt) => opt.label.toLowerCase().includes(normalizedInput));
        if (filtered.length === 0 && normalizedInput) {
          const otroOption = options.find((opt) => opt.label.toLowerCase().includes('otro'));
          if (otroOption) {
            filtered = [otroOption];
          }
        }
        return filtered;
      };

      return (
        <Autocomplete
          key={field.name}
          autoHighlight
          options={field.options || []}
          filterOptions={filterOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={currentOption}
          onChange={(_e, newValue) => {
            const value = newValue ? newValue.value : '';
            const numericFields = ['schoolId'];
            const finalValue = numericFields.includes(field.name) && value
              ? Number(value)
              : value;
            const isSelectingOtro = newValue?.label === 'Otro';
            const updates: Partial<EnrollmentFormData> = { [field.name]: finalValue };

            // Limpiar campos dependientes cuando NO se selecciona "Otro"
            if (field.name === 'schoolId' && !isSelectingOtro) {
              updates.otherSchoolName = '';
            }
            if (field.name === 'representativeCommune' && !isSelectingOtro) {
              updates.representativeOtherCommune = '';
            }
            if (field.name === 'supporterCommune' && !isSelectingOtro) {
              updates.supporterOtherCommune = '';
            }

            onFormChange(updates);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={field.label}
              variant="outlined"
              required={field.required}
              className={classes.textField}
              error={hasError}
              helperText={fieldError?.message}
              disabled={isDisabled}
            />
          )}
        />
      );
    }

    if (field.type === 'email') {
      const emailValidation = emailValidations[field.name];
      const showEmailError = hasError || (emailValidation && !emailValidation.isValid);
      const emailHelperText = emailValidation?.message || fieldError?.message;

      return (
        <TextField
          key={field.name}
          fullWidth
          label={field.label}
          type={field.type}
          variant="outlined"
          value={String(fieldValue || '')}
          onChange={handleChange(field.name, field.type)}
          placeholder={field.placeholder}
          required={field.required}
          className={classes.textField}
          error={showEmailError}
          helperText={emailHelperText}
          disabled={isDisabled}
          inputProps={{ maxLength: field.maxLength ?? MAX_FIELD_LENGTH }}
          InputProps={{
            endAdornment: emailValidation && (
              <InputAdornment position="end">
                {(() => {
                  if (emailValidation.isValidating) {
                    return <CircularProgress size={20} />;
                  }
                  if (emailValidation.isValid) {
                    return <CheckCircle color="success" />;
                  }
                  return <Error color="error" />;
                })()}
              </InputAdornment>
            ),
          }}
        />
      );
    }

    if (field.type === 'year') {
      return (
        <TextField
          key={field.name}
          fullWidth
          label={field.label}
          variant="outlined"
          value={fieldValue ?? ''}
          onChange={handleChange(field.name, field.type)}
          placeholder={field.placeholder}
          required={field.required}
          className={classes.textField}
          error={hasError}
          helperText={fieldError?.message}
          disabled={isDisabled}
          type="number"
          inputProps={{ inputMode: 'numeric', min: field.min }}
        />
      );
    }

    return (
      <TextField
        key={field.name}
        fullWidth
        label={field.label}
        type={field.type}
        variant="outlined"
        value={String(fieldValue || '')}
        onChange={handleChange(field.name, field.type)}
        placeholder={field.placeholder}
        required={field.required}
        className={classes.textField}
        error={hasError}
        helperText={fieldError?.message}
        disabled={isDisabled}
        inputProps={{ maxLength: field.maxLength ?? MAX_FIELD_LENGTH }}
      />
    );
  };

  const checkboxFields = stepConfig.fields.filter((f) => f.type === 'checkbox');
  const otherFields = stepConfig.fields.filter((f) => f.type !== 'checkbox');

  return (
    <Box className={classes.stepContainer}>
      <Box className={classes.stepHeaderContainer}>
        <Box className={classes.stepHeader}>
          <Box className={classes.stepNumber}>{stepConfig.stepNumber}</Box>
          <Typography className={classes.stepTitle}>
            {stepConfig.title}
          </Typography>
        </Box>
        {stepConfig.description && (
          <Typography color="textSecondary" className={classes.sectionDescription}>
            {stepConfig.description}
          </Typography>
        )}
      </Box>
      {checkboxFields.length > 0 && (
        <Box>
          <Box className={classes.checkboxContainer} >
            {checkboxFields.map((field) => renderField(field))}
          </Box>
          {stepConfig.stepNumber === 2 && validationErrors.find((error) => error.field === 'selectedSubjects') && (
            <Alert severity="error" sx={{ mt: 2 }} className={classes.errorAlert} >
              {validationErrors.find((error) => error.field === 'selectedSubjects')?.message}
            </Alert>
          )}
        </Box>
      )}
      <Box className={
        stepConfig.stepNumber === 2
          ? classes.fieldsContainerStep2
          : classes.fieldsContainer
      }>
        {otherFields.map((field) => renderField(field))}
      </Box>
    </Box>
  );
}

export default DynamicForm;
